import React, { useState, useEffect } from "react";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Form, 
  FormControl, 
  FormDescription, 
  FormField, 
  FormItem, 
  FormLabel, 
  FormMessage 
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { useForm } from "react-hook-form";
import { Settings, User, Receipt, Tag } from "lucide-react";
import { saveStoreSettings, getStoreSettings } from "@/lib/db/indexedDB";
import { useToast } from "@/components/ui/use-toast";

interface StoreProfileFormData {
  storeName: string;
  storeAddress: string;
  phone: string;
  email: string;
  registrationNumber: string;
  logo: FileList | null;
}

interface TaxSettingsFormData {
  enableTax: boolean;
  taxName: string;
  taxRate: number;
  taxRegistrationNumber: string;
  displayTaxOnReceipt: boolean;
  applyTaxByDefault: boolean;
}

const Profile: React.FC = () => {
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  const profileForm = useForm<StoreProfileFormData>({
    defaultValues: {
      storeName: "",
      storeAddress: "",
      phone: "",
      email: "",
      registrationNumber: "",
      logo: null,
    },
  });

  const taxForm = useForm<TaxSettingsFormData>({
    defaultValues: {
      enableTax: false,
      taxName: "GST",
      taxRate: 6,
      taxRegistrationNumber: "",
      displayTaxOnReceipt: true,
      applyTaxByDefault: true,
    },
  });

  // Load saved settings
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settings = await getStoreSettings();
        if (settings) {
          // Update profile form
          profileForm.reset({
            storeName: settings.storeName || "",
            storeAddress: settings.storeAddress || "",
            phone: settings.phone || "",
            email: settings.email || "",
            registrationNumber: settings.registrationNumber || "",
          });
          
          // Update tax form
          taxForm.reset({
            enableTax: settings.enableTax || false,
            taxName: settings.taxName || "GST",
            taxRate: settings.taxRate || 6,
            taxRegistrationNumber: settings.taxRegistrationNumber || "",
            displayTaxOnReceipt: settings.displayTaxOnReceipt || true,
            applyTaxByDefault: settings.applyTaxByDefault || true,
          });

          // Set logo preview if exists
          if (settings.logoUrl) {
            setLogoPreview(settings.logoUrl);
          }
        }
      } catch (error) {
        console.error("Error loading settings:", error);
        toast({
          title: "Error",
          description: "Failed to load store settings",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();
  }, []);

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const onProfileSubmit = async (data: StoreProfileFormData) => {
    try {
      // Save profile data with logo if exists
      await saveStoreSettings({
        ...data,
        logoUrl: logoPreview,
        lastUpdated: new Date().toISOString(),
      });
      
      toast({
        title: "Success",
        description: "Store profile updated successfully",
      });
    } catch (error) {
      console.error("Error saving profile:", error);
      toast({
        title: "Error",
        description: "Failed to save store profile",
        variant: "destructive",
      });
    }
  };

  const onTaxSubmit = async (data: TaxSettingsFormData) => {
    try {
      // Get existing settings first
      const existingSettings = await getStoreSettings();
      
      // Save tax settings while preserving other settings
      await saveStoreSettings({
        ...existingSettings,
        ...data,
        lastUpdated: new Date().toISOString(),
      });
      
      toast({
        title: "Success",
        description: "Tax settings updated successfully",
      });
    } catch (error) {
      console.error("Error saving tax settings:", error);
      toast({
        title: "Error",
        description: "Failed to save tax settings",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-green-700">Profile</h1>
      </div>
      
      <Tabs defaultValue="store-profile" className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-4">
          <TabsTrigger value="store-profile">
            <User className="h-4 w-4 mr-2" />
            Store Profile
          </TabsTrigger>
          <TabsTrigger value="tax-settings">
            <Receipt className="h-4 w-4 mr-2" />
            Tax Settings
          </TabsTrigger>
        </TabsList>
        
        {/* Store Profile Tab */}
        <TabsContent value="store-profile">
          <Card>
            <CardHeader>
              <CardTitle>Store Profile</CardTitle>
              <CardDescription>
                Manage your store information that will be displayed on receipts and your storefront.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...profileForm}>
                <form onSubmit={profileForm.handleSubmit(onProfileSubmit)} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <FormField
                        control={profileForm.control}
                        name="storeName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Store Name</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormDescription>The name of your store</FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    
                    <div>
                      <FormField
                        control={profileForm.control}
                        name="registrationNumber"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Registration Number</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormDescription>Your business registration number</FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                  
                  <FormField
                    control={profileForm.control}
                    name="storeAddress"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Store Address</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormDescription>Your store physical address</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={profileForm.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Phone Number</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={profileForm.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email Address</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  
                  <div>
                    <FormLabel>Store Logo</FormLabel>
                    <div className="flex items-center gap-4 mt-2">
                      <div 
                        className={`border-2 border-dashed rounded-md p-6 flex flex-col items-center justify-center gap-2 ${
                          logoPreview ? "border-green-500" : "border-muted"
                        }`}
                        style={{ width: "150px", height: "150px" }}
                      >
                        {logoPreview ? (
                          <img 
                            src={logoPreview} 
                            alt="Store logo preview" 
                            className="max-h-full max-w-full object-contain" 
                          />
                        ) : (
                          <div className="flex flex-col items-center justify-center">
                            <User className="h-10 w-10 text-muted-foreground" />
                            <p className="text-xs text-muted-foreground text-center mt-2">
                              No logo uploaded
                            </p>
                          </div>
                        )}
                      </div>

                      <div>
                        <Input 
                          type="file" 
                          id="store-logo" 
                          accept="image/*"
                          className="hidden"
                          onChange={handleLogoChange}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => document.getElementById("store-logo")?.click()}
                        >
                          {logoPreview ? "Change Logo" : "Upload Logo"}
                        </Button>
                        {logoPreview && (
                          <Button
                            type="button"
                            variant="ghost"
                            className="ml-2"
                            onClick={() => setLogoPreview(null)}
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground mt-2">
                      Recommended: Square image, at least 300x300 pixels
                    </p>
                  </div>
                  
                  <div className="flex justify-end space-x-2 pt-4">
                    <Button type="submit" className="bg-green-600 hover:bg-green-700">
                      Save Profile
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Tax Settings Tab */}
        <TabsContent value="tax-settings">
          <Card>
            <CardHeader>
              <CardTitle>Tax Settings</CardTitle>
              <CardDescription>
                Configure tax settings for your products and services.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...taxForm}>
                <form onSubmit={taxForm.handleSubmit(onTaxSubmit)} className="space-y-6">
                  <FormField
                    control={taxForm.control}
                    name="enableTax"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <FormLabel className="text-base">
                            Enable Tax Calculation
                          </FormLabel>
                          <FormDescription>
                            Toggle tax calculation for your store
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  
                  {taxForm.watch("enableTax") && (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <FormField
                          control={taxForm.control}
                          name="taxName"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Tax Name</FormLabel>
                              <FormControl>
                                <Input {...field} />
                              </FormControl>
                              <FormDescription>E.g., GST, VAT, Sales Tax</FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={taxForm.control}
                          name="taxRate"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Tax Rate (%)</FormLabel>
                              <FormControl>
                                <Input 
                                  type="number" 
                                  min="0" 
                                  max="100" 
                                  step="0.01"
                                  {...field}
                                  onChange={(e) => field.onChange(parseFloat(e.target.value))} 
                                />
                              </FormControl>
                              <FormDescription>Percentage rate to apply</FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      
                      <FormField
                        control={taxForm.control}
                        name="taxRegistrationNumber"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Tax Registration Number</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormDescription>Your tax registration ID number</FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <Separator />
                      
                      <FormField
                        control={taxForm.control}
                        name="displayTaxOnReceipt"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                            <div className="space-y-0.5">
                              <FormLabel className="text-base">
                                Display Tax on Receipt
                              </FormLabel>
                              <FormDescription>
                                Show tax details on customer receipts
                              </FormDescription>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={taxForm.control}
                        name="applyTaxByDefault"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                            <div className="space-y-0.5">
                              <FormLabel className="text-base">
                                Apply Tax by Default
                              </FormLabel>
                              <FormDescription>
                                Automatically apply tax to new products
                              </FormDescription>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </>
                  )}
                  
                  <div className="flex justify-end space-x-2 pt-4">
                    <Button type="submit" className="bg-green-600 hover:bg-green-700">
                      Save Tax Settings
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Profile;
